import FormattedMessage from 'I18n/components/FormattedMessage';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getUserInfo } from 'ui-addon-upgrades/_core/common/api/getUserInfo';
import UIFloatingAlertList from 'UIComponents/alert/UIFloatingAlertList';
import Raven from 'raven-js';
import withErrorBoundary from '../../ErrorBoundary';
import { RequestAccessModalWrapper } from '../../RequestAccessModalWrapper';
import { isAdmin } from '../../_core/common/eligibility/isAdmin';
import { navigateTo } from '../../_core/common/navigation/url';
import { requestSKU } from '../../_core/upgrade/requestSKU';
import { isQuantityPackApiName } from '../../_core/utils/productUtils';
import { generateCheckoutUrl } from './buy-now-api';
import { getBuyNowUsageTrackingProperties, trackClickedBuyNowButton, trackViewBuyNowButton } from './buy-now-trackers';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const _UIBuyNowButton = ({
  block,
  children,
  className,
  currencyCode,
  'data-test-id': dataTestId,
  'data-selenium-id': dataSeleniumId,
  disabled = false,
  FailedPurchaseOrderCallToAction,
  onBuyNowFailure,
  onClick,
  requestedProducts,
  shouldDisplayAlertOnFailure = false,
  size,
  style,
  terms,
  trackingProperties,
  use = 'primary',
  userInfo: passedUserInfo,
  redirectQueryParams
}) => {
  const buyNowUsageTrackingProperties = getBuyNowUsageTrackingProperties(trackingProperties);
  const requestAccessModalTrackingProperties = Object.assign({}, buyNowUsageTrackingProperties, {
    modalType: 'requestSKU'
  });
  const [userInfo, setUserInfo] = useState(passedUserInfo);
  const [isLoadingUserState, setIsLoadingUserState] = useState(!passedUserInfo);
  const [isGeneratingPurchaseOrderId, setIsGeneratingPurchaseOrderId] = useState(false);
  const [localAlertStore] = useState(() => {
    return FloatingAlertStore.newFloatingAlertStore();
  });
  const hasPermissionToBuyNow = useMemo(() => {
    return !!userInfo && isAdmin(userInfo.user, 'billing');
  }, [userInfo]);
  const foundApiName = requestedProducts === null || requestedProducts === void 0 ? void 0 : requestedProducts.map(({
    productApiName
  }) => productApiName).find(apiName => !isQuantityPackApiName(apiName));
  const handleRequestAccess = useCallback(() => {
    if (!foundApiName) {
      return Promise.reject(new Error('No product api name provided to requestSKU'));
    }
    return requestSKU({
      apiName: foundApiName
    });
  }, [foundApiName]);
  const handleClickBuyNow = useCallback(() => {
    setIsGeneratingPurchaseOrderId(true);
    if (onClick) {
      onClick();
    }
    trackClickedBuyNowButton(buyNowUsageTrackingProperties);
    generateCheckoutUrl({
      buyNowUsageTrackingProperties,
      currencyCode,
      requestedProducts,
      terms
    }).then(checkoutUrl => {
      if (!checkoutUrl) {
        Raven.captureException(new Error('No checkout url provided from generateCheckoutUrl'));
        return;
      }
      const url = new URL(checkoutUrl);

      // add any redirect params to the checkout URL if provided
      if (redirectQueryParams) {
        Object.entries(redirectQueryParams).forEach(([key, value]) => {
          url.searchParams.append(key, value.toString());
        });
      }
      navigateTo(url.href);
    }).catch(error => {
      if (shouldDisplayAlertOnFailure) {
        localAlertStore.addAlert({
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "upgrades.buyNowFailureAlert.title"
          }),
          message: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "upgrades.buyNowFailureAlert.message"
            }), FailedPurchaseOrderCallToAction || null]
          }),
          type: 'danger'
        });
      }
      if (onBuyNowFailure) {
        onBuyNowFailure(error);
      }
    }).finally(() => {
      setIsGeneratingPurchaseOrderId(false);
    });
  }, [onClick, buyNowUsageTrackingProperties, currencyCode, requestedProducts, terms, redirectQueryParams, shouldDisplayAlertOnFailure, onBuyNowFailure, localAlertStore, FailedPurchaseOrderCallToAction]);
  useEffect(() => {
    if (!passedUserInfo) {
      void getUserInfo().then(setUserInfo).finally(() => setIsLoadingUserState(false));
    }
  }, [passedUserInfo]);
  useEffect(() => {
    trackViewBuyNowButton(buyNowUsageTrackingProperties);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (isLoadingUserState) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UIFloatingAlertList, {
      alertStore: localAlertStore
    }), hasPermissionToBuyNow ? /*#__PURE__*/_jsx(UILoadingButton, {
      block: block,
      className: className,
      "data-test-id": dataTestId || 'buy-now-button',
      "data-selenium-id": dataSeleniumId,
      disabled: disabled,
      loading: isGeneratingPurchaseOrderId,
      onClick: handleClickBuyNow,
      size: size,
      style: style,
      use: use,
      children: children || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.checkoutButton.default"
      })
    }) : /*#__PURE__*/_jsx(RequestAccessModalWrapper, {
      buttonBlock: block,
      buttonClassName: className,
      buttonSize: size,
      buttonStyle: style,
      buttonSeleniumId: dataSeleniumId,
      buttonTestId: dataTestId || 'buy-now-button',
      buttonUse: use,
      floatingAlertStore: localAlertStore,
      requestAccessPromise: handleRequestAccess,
      usageTrackingProperties: requestAccessModalTrackingProperties,
      OpenModalButtonLabel: children || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.checkoutButton.default"
      }),
      ModalBody: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.requestAccessModalWrapper.buyNow.description"
      }),
      ModalTitle: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.requestAccessModalWrapper.buyNow.title"
      }),
      modalUse: "success",
      modalWidth: 540,
      SubmitRequestButtonLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.requestAccessModalWrapper.buyNow.button"
      })
    })]
  });
};
export const UIBuyNowButton = withErrorBoundary(_UIBuyNowButton, {
  boundaryName: 'UIBuyNowButton'
});